/**
 * EmailEditor
 * 
 * Email draft editor with recipient fields, subject, and body
 */

import React, { useCallback } from 'react';
import type { EmailArtifact } from '../../artifacts';
import { useWorkspace } from '../../artifacts';

interface EmailEditorProps {
  artifact: EmailArtifact;
}

export const EmailEditor: React.FC<EmailEditorProps> = ({ artifact }) => {
  const { updateArtifact } = useWorkspace();

  const handleUpdate = useCallback((updates: Partial<EmailArtifact>) => {
    updateArtifact(artifact.id, updates);
  }, [artifact.id, updateArtifact]);

  const handleToChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const emails = e.target.value.split(',').map((s) => s.trim());
    handleUpdate({ to: emails });
  }, [handleUpdate]);

  const handleCcChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const emails = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
    handleUpdate({ cc: emails.length > 0 ? emails : undefined });
  }, [handleUpdate]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Email header fields */}
      <div className="border-b border-neutral-200">
        {/* To field */}
        <div className="flex items-center border-b border-neutral-100">
          <label className="w-16 px-4 py-3 text-sm font-medium text-neutral-500 bg-neutral-50">
            To
          </label>
          <input
            type="text"
            value={artifact.to.join(', ')}
            onChange={handleToChange}
            className="flex-1 px-3 py-3 text-sm text-neutral-900 outline-none"
            placeholder="recipient@example.com"
          />
        </div>

        {/* CC field */}
        <div className="flex items-center border-b border-neutral-100">
          <label className="w-16 px-4 py-3 text-sm font-medium text-neutral-500 bg-neutral-50">
            Cc
          </label>
          <input
            type="text"
            value={artifact.cc?.join(', ') || ''}
            onChange={handleCcChange}
            className="flex-1 px-3 py-3 text-sm text-neutral-900 outline-none"
            placeholder="Optional: cc@example.com"
          />
        </div>

        {/* Subject field */}
        <div className="flex items-center">
          <label className="w-16 px-4 py-3 text-sm font-medium text-neutral-500 bg-neutral-50">
            Subject
          </label>
          <input
            type="text"
            value={artifact.subject}
            onChange={(e) => handleUpdate({ subject: e.target.value })}
            className="flex-1 px-3 py-3 text-sm text-neutral-900 font-medium outline-none"
            placeholder="Email subject"
          />
        </div>
      </div>

      {/* Email body */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <textarea
          value={artifact.body}
          onChange={(e) => handleUpdate({ body: e.target.value })}
          className="w-full h-full p-4 text-sm text-neutral-800 resize-none outline-none leading-relaxed"
          placeholder="Write your email here...

Start typing or ask the assistant to draft content for you."
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-200 bg-neutral-50">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            Send
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors"
          >
            Save Draft
          </button>
        </div>
        <div className="flex items-center gap-3 text-xs text-neutral-500">
          <span>
            Status: <span className="font-medium text-amber-600">{artifact.status}</span>
          </span>
          {artifact.citations && artifact.citations.length > 0 && (
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              {artifact.citations.length} citation{artifact.citations.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailEditor;

